import logoBig from '@/assets/big-logo.svg'
import { CORREO_CAPITAL_HUMANO } from '@/features/rh/components/AvisoDePrivacidadSimplificado'

// Aviso de privacidad integral para candidatos y postulantes. Página pública
// (sin sesión): se abre desde el formulario de postulación. El texto es el que
// mandó Capital Humano; los derechos ARCO se separan en una lista para leerlos.

const Titulo = ({ children }: { children: string }) => (
  <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">{children}</h2>
)

const Lista = ({ elementos }: { elementos: string[] }) => (
  <ul className="list-disc pl-6 space-y-1.5">
    {elementos.map((e) => (
      <li key={e}>{e}</li>
    ))}
  </ul>
)

const Correo = () => (
  <a href={`mailto:${CORREO_CAPITAL_HUMANO}`} className="text-blue-600 hover:underline">
    {CORREO_CAPITAL_HUMANO}
  </a>
)

const AvisoDePrivacidadIntegral = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4">
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-center mb-8">
        <img src={logoBig} alt="SLYRSA Logo" className="h-20 w-auto" />
      </div>

      <article className="bg-white rounded-2xl shadow-xl p-8 text-sm text-gray-700 leading-relaxed space-y-3">
        <h1 className="text-2xl font-bold text-gray-900">
          Aviso de Privacidad Integral para Candidatos y Postulantes
        </h1>

        <p>
          Slyrsa Grupo Industrial, S.A. de C.V. (en lo sucesivo «SLYRSA»), con domicilio en Calle
          Violeta No. 1982, Colonia Moderna, C.P. 64530, Monterrey, Nuevo León, México, es el
          responsable del uso y protección de sus datos personales, y al respecto le informamos lo
          siguiente:
        </p>

        <Titulo>Finalidades del tratamiento</Titulo>
        <p>
          Los datos personales que recopilamos a través de este portal de postulación serán
          utilizados para las siguientes finalidades, las cuales son necesarias para el proceso de
          selección:
        </p>
        <Lista
          elementos={[
            'Gestionar su participación en los procesos de reclutamiento y selección de vacantes actuales o futuras.',
            'Evaluar sus aptitudes, capacidades, historial académico y trayectoria laboral para determinar su elegibilidad al puesto solicitado.',
            'Validar las referencias laborales y personales que nos proporcione.',
            'Contactarle para entrevistas, evaluaciones psicométricas o requerimientos de información adicional relacionados con su postulación.',
            'Integrar su expediente de candidato en nuestra base de datos interna de Recursos Humanos.',
          ]}
        />
        <p>
          De manera adicional, utilizaremos su información para las siguientes finalidades
          secundarias, que no son estrictamente necesarias, pero nos permiten brindarle una mejor
          atención:
        </p>
        <Lista
          elementos={[
            'Conservar su perfil en nuestra cartera de talento para futuras vacantes operativas o de ingeniería que se ajusten a su perfil profesional.',
          ]}
        />
        <p>
          Si no desea que sus datos personales se utilicen para esta finalidad secundaria, puede
          manifestarlo enviando un correo electrónico a <Correo />.
        </p>

        <Titulo>Datos personales</Titulo>
        <p>
          Para llevar a cabo las finalidades descritas en el presente aviso de privacidad,
          recabaremos las siguientes categorías de datos directamente de los formularios o del
          Curriculum Vitae que usted adjunte:
        </p>
        <Lista
          elementos={[
            'Datos de identificación y contacto: nombre completo, fecha de nacimiento, estado civil, CURP, RFC, dirección de correo electrónico, teléfono fijo o celular, y domicilio.',
            'Datos académicos: trayectoria escolar, títulos, certificaciones, cursos y conocimientos técnicos.',
            'Datos laborales: historial de empleo, puestos desempeñados, responsabilidades, referencias laborales y expectativas económicas.',
            'Documentos adjuntos: archivos en PDF o Word con su currículum o solicitudes de empleo digitales.',
          ]}
        />

        <Titulo>Tratamiento de datos personales sensibles</Titulo>
        <p>
          SLYRSA no solicita datos personales sensibles (como origen étnico, estado de salud
          presente o futuro, creencias religiosas o preferencia sexual) en la etapa inicial de
          postulación digital. En caso de requerirse evaluaciones médicas o psicométricas
          posteriores como parte de nuestro proceso de contratación industrial, se le solicitará su
          consentimiento en el momento adecuado.
        </p>

        <Titulo>Transferencia de datos personales</Titulo>
        <p>
          Le informamos que sus datos personales no serán compartidos, vendidos ni transferidos a
          terceras personas ajenas a la organización sin su consentimiento explícito, salvo por los
          requerimientos legales o de autoridades competentes previstas en el Artículo 37 de la Ley
          Federal de Protección de Datos Personales en Posesión de los Particulares.
        </p>

        <Titulo>Derechos ARCO</Titulo>
        <p>Usted tiene derecho a:</p>
        <Lista
          elementos={[
            'Acceso: conocer qué datos personales tenemos de usted, para qué los utilizamos y las condiciones del uso que les damos.',
            'Rectificación: solicitar la corrección de su información personal en caso de que esté desactualizada, sea inexacta o incompleta.',
            'Cancelación: solicitar que la eliminemos de nuestros registros o bases de datos cuando considere que no está siendo utilizada adecuadamente.',
            'Oposición: oponerse al uso de sus datos personales para fines específicos.',
          ]}
        />
        <p>
          Para el ejercicio de cualquiera de los Derechos ARCO, usted deberá enviar una solicitud
          por escrito dirigida a nuestro Departamento de Capital Humano al correo electrónico
          oficial: <Correo />. Dicha solicitud deberá contener al menos: nombre completo del
          titular, documentos que acrediten su identidad y la descripción clara de los datos sobre
          los que busca ejercer el derecho.
        </p>

        <Titulo>Cambios al aviso de privacidad</Titulo>
        <p>
          SLYRSA se reserva el derecho de modificar o actualizar el presente Aviso de Privacidad
          para atender modificaciones legales, políticas internas o nuevos requerimientos. Cualquier
          cambio será publicado en{' '}
          <a
            href="https://www.slyrsa.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline"
          >
            www.slyrsa.com
          </a>{' '}
          o en el portal correspondiente de reclutamiento.
        </p>

        <Titulo>Consentimiento</Titulo>
        <p>
          Al proporcionar sus datos personales y postularse a una vacante, usted reconoce haber
          leído y comprendido el presente Aviso de Privacidad y consiente el tratamiento de sus
          datos conforme a los términos aquí establecidos.
        </p>
      </article>
    </div>
  </div>
)

export default AvisoDePrivacidadIntegral
